import React from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DATE_TIME_FORMAT_ISO } from '@/constant';
import { TextFieldType } from '@/type';
import InputAdornment from '@mui/material/InputAdornment';
import { buildFormFieldLabel } from '@/util';
import { StringSchema } from 'yup';
import { hu } from 'date-fns/locale';

interface DateTimePickerInputProps {
  field?: any;
  dateField?: TextFieldType;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const DateTimePickerInput = ({
  field,
  dateField,
  error,
  helperText,
  disabled,
}: DateTimePickerInputProps) => {
  const { t } = useTranslation();

  const handleDateChange = (date: Date | null) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      const formattedDateTime = format(date, DATE_TIME_FORMAT_ISO);
      field.onChange(formattedDateTime);
    } else {
      field.onChange('');
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={hu}
      localeText={{ todayButtonLabel: t('common.form.now') }}
    >
      <DateTimePicker<Date>
        id={field.name}
        {...field}
        className='datetime-input'
        value={field.value ? parseISO(field.value) : null}
        label={buildFormFieldLabel(
          t(dateField.label),
          dateField.validation as StringSchema,
        )}
        disabled={disabled || dateField.disabled}
        defaultValue={dateField.defaultValue}
        minDateTime={dateField.inputProps?.min}
        maxDateTime={dateField.inputProps?.max}
        onChange={handleDateChange}
        sx={{ width: '100%' }}
        slotProps={{
          textField: {
            error: !!error,
            helperText,
            InputLabelProps: {
              shrink: true,
            },
            InputProps: {
              startAdornment: dateField.preFix ? (
                <InputAdornment position='start'>{dateField.preFix}</InputAdornment>
              ) : null,
            },
          },
          openPickerButton: {
            className: 'custom-calendar-button',
          },
          actionBar: {
            actions: ['today', 'accept'],
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default DateTimePickerInput;
